/**
 * Sync Recovery Manager
 * Extracted from WebSocketManager.ts
 * Handles error recovery and full sync operations
 */

import { log } from "@/shared/utils/logger";

/** The real provider must return completion, not fire-and-forget refreshes. */
export interface ProjectRecoveryBridge {
  getRecoveryOwnerEpoch: () => string | null;
  reloadProjects: (projectIds?: string[], expectedOwnerEpoch?: string) => Promise<void>;
  reloadAllProjects: () => Promise<void>;
}

export interface SyncRecoveryConfig {
  maxUnparseableMessages: number;
  maxMessageSize: number;
  /** Merge these recovery-only status updates with outbound state in the owner. */
  onSyncStateChange: (updates: Record<string, unknown>) => void;
  getCurrentProjectIds?: () => string[];
  getOwnerGeneration?: () => number;
  reloadProjects?: (projectIds?: string[]) => Promise<void>;
}

export interface RecoveryState {
  status: "idle" | "syncing" | "synced" | "conflict";
  projectIds: string[];
  error?: string;
}

type RecoveryRun = {
  generation: number | string | null;
  pending: Set<string>;
  full: boolean;
  failed: Set<string>;
  failedFull: boolean;
  lastError?: string;
  state: RecoveryState;
  promise: Promise<void> | null;
};

const getBridge = (): Partial<ProjectRecoveryBridge> | undefined =>
  typeof window === "undefined" ? undefined :
    (window as unknown as { __multiProjectContext?: Partial<ProjectRecoveryBridge> }).__multiProjectContext;

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown> : undefined;

export class SyncRecoveryManager {
  private unprocessableMessages: Array<{
    data: string;
    error: Error;
    timestamp: number;
  }> = [];
  private config: SyncRecoveryConfig;
  private recovery: RecoveryRun | null = null;

  constructor(config: SyncRecoveryConfig) {
    this.config = config;
  }

  // Record unparseable message
  recordUnparseableMessage(data: string, error: Error): boolean {
    this.unprocessableMessages.push({
      data: data.substring(0, this.config.maxMessageSize),
      error,
      timestamp: Date.now(),
    });

    log.error("WebSocket", "Failed to parse WebSocket message:", error);

    // Check if too many parse failures
    if (
      this.unprocessableMessages.length > this.config.maxUnparseableMessages
    ) {
      log.warn(
        "WebSocket",
        `Too many unparseable messages (${this.unprocessableMessages.length}), initiating full sync`
      );
      // Keep only most recent unparseable messages
      this.unprocessableMessages = this.unprocessableMessages.slice(-5);
      return true; // Indicates full sync needed
    }

    return false;
  }

  recoverFailedMessage(message: Record<string, unknown>): Promise<void> {
    if (message.type !== "operation-result" && message.type !== "operation") {
      return Promise.resolve();
    }
    const operation = asRecord(message.operation);
    const nested = asRecord(operation?.operation);
    const projectId = [message.projectId, message.project_id, operation?.projectId,
      operation?.project_id, nested?.projectId, nested?.project_id]
      .find((id): id is string => typeof id === "string" && Boolean(id.trim()));
    return this.initiateFullSync(projectId ? [projectId] : undefined);
  }

  private getGeneration(): number | string | null {
    return this.config.getOwnerGeneration?.() ?? getBridge()?.getRecoveryOwnerEpoch?.() ?? null;
  }

  private current(run: RecoveryRun): boolean {
    return this.recovery === run && run.generation === this.getGeneration();
  }

  private getRun(): RecoveryRun {
    const generation = this.getGeneration();
    if (!this.recovery || this.recovery.generation !== generation) {
      this.recovery = {
        generation, pending: new Set(), full: false, failed: new Set(), failedFull: false,
        state: { status: "idle", projectIds: [] }, promise: null,
      };
    }
    return this.recovery;
  }

  /** Returns only this owner's recovery state; never an outbound queue snapshot. */
  getRecoveryState(): RecoveryState {
    const state = this.getRun().state;
    return { ...state, projectIds: [...state.projectIds] };
  }

  private publish(run: RecoveryRun, state: RecoveryState): void {
    if (!this.current(run)) return;
    run.state = state;
    // Do not reset pendingOperations, versions, or timestamps. The manager owns
    // the status overlay and must preserve outbound pending/conflict on success.
    this.config.onSyncStateChange({ syncStatus: state.status });
  }

  /** Coalesces reads only. This helper has no mutation transport or replay API. */
  initiateFullSync(projectIds?: string[]): Promise<void> {
    const run = this.getRun();
    const targets = projectIds ?? this.config.getCurrentProjectIds?.();
    if (targets?.length) {
      targets.filter(id => typeof id === "string" && Boolean(id.trim())).forEach(id => run.pending.add(id));
    } else {
      run.full = true; // Provider fallback includes visible AND active projects.
    }
    if (!run.pending.size && !run.full) run.full = true;
    return this.schedule(run);
  }

  /** Retry failed reads explicitly; never repeat an uncertain outbound write. */
  retryRecovery(): Promise<void> {
    const run = this.getRun();
    if (run.promise) return run.promise;
    if (!run.failed.size && !run.failedFull) return Promise.resolve();
    run.failed.forEach(id => run.pending.add(id));
    run.full = run.failedFull;
    return this.schedule(run);
  }

  private schedule(run: RecoveryRun): Promise<void> {
    if (run.promise) return run.promise;
    // Deferral batches synchronous failures; requests during an active read
    // remain pending for one trailing read of the newly damaged projects.
    run.promise = Promise.resolve().then(() => this.drain(run)).finally(() => {
      run.promise = null;
      // A status callback or queued microtask may request another read after
      // drain settles but before this finalizer. Keep its returned promise live.
      if (this.current(run) && (run.full || run.pending.size)) return this.schedule(run);
    });
    return run.promise;
  }

  private async reload(projectIds?: string[]): Promise<void> {
    let completion: Promise<void> | undefined;
    if (this.config.reloadProjects) {
      completion = this.config.reloadProjects(projectIds);
    } else {
      const bridge = getBridge();
      const epoch = bridge?.getRecoveryOwnerEpoch?.();
      if (!epoch || !bridge?.reloadProjects) throw new Error("Project recovery bridge is unavailable for the current owner");
      completion = bridge.reloadProjects(projectIds, epoch);
    }
    if (!completion || typeof completion.then !== "function") {
      throw new Error("Project recovery bridge did not return reload completion");
    }
    await completion;
  }

  private async drain(run: RecoveryRun): Promise<void> {
    while (this.current(run) && (run.full || run.pending.size)) {
      const full = run.full;
      const targets = full ? [] : [...run.pending];
      run.full = false;
      if (!full) run.pending.clear();
      this.publish(run, { status: "syncing", projectIds: targets });
      // A callback may synchronously invalidate the owner.
      if (!this.current(run)) return;
      try {
        await this.reload(full ? undefined : targets);
        if (!this.current(run)) return;
        if (full) {
          run.failedFull = false;
          // A provider-wide read does not prove older explicit hidden targets
          // were included. Only explicit successful reads retire those failures.
        } else {
          targets.forEach(id => run.failed.delete(id));
        }
      } catch (error) {
        if (!this.current(run)) return;
        targets.forEach(id => run.failed.add(id));
        run.failedFull ||= full;
        run.lastError = error instanceof Error ? error.message : String(error);
        log.error("WebSocket", "Authoritative project recovery failed:", error);
      }
    }
    if (!this.current(run)) return;
    if (run.failedFull || run.failed.size) {
      this.publish(run, { status: "conflict", projectIds: [...run.failed], error: run.lastError });
    } else {
      this.unprocessableMessages = [];
      this.publish(run, { status: "synced", projectIds: [] });
    }
  }

  // Get unparseable message history
  getUnparseableMessages(): Array<{
    data: string;
    error: Error;
    timestamp: number;
  }> {
    return [...this.unprocessableMessages];
  }

  // Clear unparseable message history
  clearUnparseableMessages(): void {
    this.unprocessableMessages = [];
  }
}
